import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendUp, CurrencyEur, FileText, Sparkle, Bell, CheckCircle } from '@phosphor-icons/react';

/**
 * Animated tech-style hero visual replacing the static "vista mare" photo.
 * Shows a fake host dashboard with:
 *  - rising bar chart (income last 6 months)
 *  - live counter (€ this month)
 *  - line chart drawing itself
 *  - floating notification cards
 * All animations are CSS + framer-motion, no external assets.
 */
export default function HeroTechVisual() {
  const [counter, setCounter] = useState(0);
  const target = 4820;

  useEffect(() => {
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setCounter(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Bar heights in % (6 months growth)
  const bars = [28, 42, 38, 55, 68, 88];
  const labels = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu'];

  // Line chart path
  const linePath = "M 8,90 C 40,80 60,60 90,55 S 140,35 180,30 S 240,18 280,12";

  return (
    <div className="relative w-full h-[440px]" data-testid="hero-tech-visual">
      {/* Background soft blobs */}
      <div className="absolute -top-6 -right-8 w-56 h-56 bg-gradient-to-br from-teal-300/40 to-emerald-200/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-8 -left-6 w-52 h-52 bg-gradient-to-tr from-amber-200/40 to-rose-100/30 rounded-full blur-3xl" />

      {/* Main dashboard card */}
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="absolute inset-0 bg-white/95 backdrop-blur-md rounded-3xl shadow-[0_20px_60px_-15px_rgba(24,24,27,0.25)] border border-zinc-200/70 p-6 overflow-hidden"
      >
        {/* Top nav dots (macOS window) */}
        <div className="flex items-center gap-1.5 mb-5">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <span className="ml-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Dashboard host · giu 2026</span>
        </div>

        {/* KPI counter */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-5"
        >
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Incassi 2026</p>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black text-zinc-900 tracking-tight tabular-nums">
              € {counter.toLocaleString('it-IT')}
            </span>
            <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
              <TrendUp size={11} weight="bold" /> +38%
            </span>
          </div>
        </motion.div>

        {/* Bar chart */}
        <div className="mb-5">
          <div className="flex items-end justify-between gap-1.5 h-[100px]">
            {bars.map((h, i) => (
              <motion.div
                key={i}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 0.35 + i * 0.09, duration: 0.55, ease: [0.34, 1.56, 0.64, 1] }}
                style={{ transformOrigin: 'bottom', height: `${h}%` }}
                className="flex-1 rounded-t-lg bg-gradient-to-t from-teal-600 via-teal-500 to-emerald-400 relative"
              >
                {i === bars.length - 1 && (
                  <motion.span
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1 }}
                    className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-black text-teal-700"
                  >
                    €4,8k
                  </motion.span>
                )}
              </motion.div>
            ))}
          </div>
          <div className="flex justify-between mt-1.5 px-0.5">
            {labels.map((l) => (
              <span key={l} className="text-[9px] font-semibold text-zinc-400 uppercase tracking-wider">{l}</span>
            ))}
          </div>
        </div>

        {/* Line chart drawing */}
        <div className="pt-4 border-t border-zinc-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Occupancy Rate</p>
            <span className="text-[10px] font-black text-emerald-600">82%</span>
          </div>
          <svg viewBox="0 0 288 100" className="w-full h-[70px]" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0d9488" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Area fill */}
            <motion.path
              d={`${linePath} L 280,100 L 8,100 Z`}
              fill="url(#lineGrad)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.6 }}
            />
            {/* Line */}
            <motion.path
              d={linePath}
              fill="none"
              stroke="#0d9488"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1.0, duration: 1.6, ease: 'easeInOut' }}
            />
            {/* Endpoint dot */}
            <motion.circle
              cx="280" cy="12" r="4" fill="#0d9488"
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.6, 1] }}
              transition={{ delay: 2.5, duration: 0.5 }}
            />
            <motion.circle
              cx="280" cy="12" r="4" fill="#0d9488" opacity="0.35"
              initial={{ scale: 1 }}
              animate={{ scale: [1, 2.6, 1] }}
              transition={{ delay: 2.7, duration: 1.4, repeat: Infinity }}
            />
          </svg>
        </div>
      </motion.div>

      {/* Floating notification card — top-right */}
      <motion.div
        initial={{ opacity: 0, x: 30, rotate: 4 }}
        animate={{ opacity: 1, x: 0, rotate: 4 }}
        transition={{ delay: 0.8, duration: 0.6, type: 'spring' }}
        className="absolute -top-3 -right-3 sm:-right-4 bg-zinc-900 text-white rounded-2xl shadow-2xl px-3.5 py-2.5 flex items-center gap-2.5"
      >
        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
          <CurrencyEur size={14} weight="bold" />
        </div>
        <div>
          <p className="text-xs font-black leading-tight">+€ 450,00</p>
          <p className="text-[9px] text-zinc-400 leading-tight">Bonifico Airbnb ricevuto</p>
        </div>
      </motion.div>

      {/* Floating contract card — bottom-left */}
      <motion.div
        initial={{ opacity: 0, y: 30, rotate: -3 }}
        animate={{ opacity: 1, y: 0, rotate: -3 }}
        transition={{ delay: 1.1, duration: 0.6, type: 'spring' }}
        className="absolute -bottom-3 -left-3 bg-white rounded-2xl shadow-2xl px-3.5 py-2.5 flex items-center gap-2.5 border border-zinc-100"
      >
        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
          <FileText size={14} weight="bold" className="text-teal-700" />
        </div>
        <div>
          <p className="text-xs font-black text-zinc-900 leading-tight flex items-center gap-1">
            Contratto firmato <CheckCircle size={12} weight="fill" className="text-emerald-500" />
          </p>
          <p className="text-[9px] text-zinc-400 leading-tight">Check-in domani 15:00</p>
        </div>
      </motion.div>

      {/* Floating "AI Import" chip — mid right, bounces gently */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0, y: [0, -6, 0] }}
        transition={{
          opacity: { delay: 1.4, duration: 0.6 },
          x: { delay: 1.4, duration: 0.6 },
          y: { delay: 2.0, duration: 2.8, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute top-[42%] -right-2 bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white rounded-full shadow-xl pl-2 pr-3 py-1.5 flex items-center gap-1.5"
      >
        <Sparkle size={12} weight="fill" className="text-amber-300" />
        <span className="text-[10px] font-black tracking-wide">AI IMPORT</span>
      </motion.div>

      {/* Floating "419 eventi" chip — mid left */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute top-[62%] -left-3 bg-white rounded-full shadow-xl pl-1.5 pr-3 py-1.5 flex items-center gap-1.5 border border-rose-200"
      >
        <span className="w-5 h-5 bg-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
          <Bell size={9} weight="fill" className="text-white" />
        </span>
        <span className="text-[10px] font-black text-zinc-900">419 eventi 2027</span>
      </motion.div>
    </div>
  );
}
